<template>
  <section>
    <b-table
      :data="items"
      :loading="sending"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
    >
      <b-table-column
        field="id"
        :label="$t('attributes.id')"
        :visible="false"
        v-slot="props"
      >
        {{ props.row.id }}
      </b-table-column>

      <b-table-column
        field="membership"
        :label="$t('attributes.membership')"
        :width="250"
        v-slot="props"
      >
        <p v-if="!props.row.editing && props.row.id">
          {{ props.row.membership }}
        </p>
        <b-field
          v-else
          :type="!!props.row.modify.errors['membership'] ? 'is-danger' : ''"
          :message="props.row.modify.errors['membership']"
        >
          <b-input
            v-model="props.row.modify.membership"
            :placeholder="$t('attributes.membership')"
            @input="props.row.modify.errors['membership'] = null"
          ></b-input>
        </b-field>
      </b-table-column>

      <b-table-column
        field="subsidy"
        :label="$t('attributes.subsidy')"
        v-slot="props"
      >
        <p v-if="!props.row.editing && props.row.id">
          {{ props.row.subsidy }}
        </p>
        <b-field
          v-else
          :type="!!props.row.modify.errors['subsidy'] ? 'is-danger' : ''"
          :message="props.row.modify.errors['subsidy']"
        >
          <b-input
            v-model.number="props.row.modify.subsidy"
            type="number"
            min="0"
            :placeholder="$t('attributes.subsidy')"
            @input="props.row.modify.errors['subsidy'] = null"
          ></b-input>
        </b-field>
      </b-table-column>

      <b-table-column :width="180" v-slot="props">
        <div v-if="!props.row.id" class="buttons">
          <b-button type="is-warning" @click.prevent="onAdd(props.row)">
            {{ $t('attributes.add') }}
          </b-button>
        </div>
        <div v-else-if="!props.row.editing" class="buttons">
          <b-button type="is-primary" @click.prevent="editing(props.row)">
            {{ $t('attributes.modify') }}
          </b-button>
          <b-button type="is-danger" @click.prevent="onDelete(props.row)">
            {{ $t('attributes.delete') }}
          </b-button>
        </div>
        <div v-else class="buttons">
          <b-button type="is-primary" @click.prevent="doneEdit(props.row)">
            {{ $t('attributes.update') }}
          </b-button>
          <b-button
            type="is-success"
            outlined
            @click.prevent="cancelEdit(props.row)"
          >
            {{ $t('attributes.cancel') }}
          </b-button>
        </div>
      </b-table-column>
    </b-table>
  </section>
</template>

<script>
import { FIRM_COLLABORATES_ACTIVITIES_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'

export default {
  name: 'ActivitySubsidyForm',

  props: {
    id: {
      required: true,
      type: Number,
      default: null
    }
  },
  data() {
    return {
      sending: false,
      items: []
    }
  },
  watch: {
    id() {
      this.fetchData()
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      const api = `${FIRM_COLLABORATES_ACTIVITIES_API}/${this.id}/subsidies`

      this.sending = true
      try {
        const res = await this.axios.get(api)
        const data = res.data.data

        this.items = data.map(item => ({
          ...item,
          editing: false,
          modify: {
            membership: item.membership,
            member_status: item.member_status,
            subsidy: item.subsidy,
            errors: {}
          }
        }))
        this.addNewSubsidyItem()
      } catch (e) {
        this.items = []

        notifyService.fetch_error('collaborate_subsidy_list', e)
      } finally {
        this.sending = false
      }
    },
    addNewSubsidyItem() {
      this.items.push({
        id: null,
        membership: '',
        member_status: '',
        subsidy: '',
        modify: {
          membership: '',
          member_status: '',
          subsidy: '',
          errors: {}
        }
      })
    },
    isValid(item) {
      let valid = true
      const errors = {
        membership: null,
        subsidy: null
      }

      if (!item.modify.membership) {
        valid = false
        errors['membership'] = '欄位不可空白'
      } else if (item.modify.membership.length > 50) {
        valid = false
        errors['membership'] = '長度不可超過50'
      } else {
        errors['membership'] = null
      }

      if (item.modify.subsidy == null || item.modify.subsidy === '') {
        valid = false
        errors['subsidy'] = '欄位不可空白'
      } else {
        const subsidy = parseInt(item.modify.subsidy)

        if (subsidy < 0) {
          valid = false
          errors['subsidy'] = '值必須大於或等於 0。'
        }
      }

      const idx = this.items.findIndex(i => i.id === item.id)
      this.items[idx].modify.errors = errors
      return valid
    },
    onAdd(item) {
      if (!this.isValid(item)) {
        return
      }

      const data = {
        membership: item.modify.membership,
        member_status: item.modify.member_status,
        subsidy: item.modify.subsidy
      }
      this.createItem(data)
    },
    editing(item) {
      item.editing = true
    },
    doneEdit(item) {
      if (!this.isValid(item)) {
        return
      }

      const data = {
        membership: item.modify.membership,
        member_status: item.modify.member_status,
        subsidy: item.modify.subsidy
      }
      this.updateItem(item.id, data)
    },
    cancelEdit(item) {
      item.editing = false
      item.modify = {
        membership: item.membership,
        member_status: item.member_status,
        subsidy: item.subsidy,
        errors: {}
      }
    },
    onDelete(item) {
      this.$buefy.dialog.confirm({
        message: this.$t('attributes.confirm_delete'),
        onConfirm: () => {
          this.deleteItem(item.id)
        }
      })
    },
    async createItem(item) {
      const api = `${FIRM_COLLABORATES_ACTIVITIES_API}/${this.id}/subsidies`

      const config = {
        errorHandle: {
          customPrefix: `${this.$t('attributes.add_failed')}: `
        }
      }

      this.sending = true
      try {
        const res = await this.axios.post(api, item, config)

        const data = res.data.data

        this.items = this.items.filter(i => i.id)
        this.items.push({
          ...data,
          editing: false,
          modify: {
            membership: data.membership,
            member_status: data.member_status,
            subsidy: data.subsidy,
            errors: {}
          }
        })
        this.addNewSubsidyItem()

        notifyService.success(this.$t('attributes.add_succeed'))
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateItem(id, item) {
      const api = `${FIRM_COLLABORATES_ACTIVITIES_API}/subsidies/${id}`

      this.sending = true
      try {
        const res = await this.axios.put(api, item)
        const data = res.data.data
        const idx = this.items.findIndex(i => i.id === id)
        this.items[idx].editing = false
        this.items[idx].membership = data.membership
        this.items[idx].member_status = data.member_status
        this.items[idx].subsidy = data.subsidy
        this.items[idx].modify = {
          membership: data.membership,
          member_status: data.member_status,
          subsidy: data.subsidy,
          errors: {}
        }
        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    async deleteItem(id) {
      const api = `${FIRM_COLLABORATES_ACTIVITIES_API}/subsidies/${id}`

      this.sending = true
      try {
        await this.axios.delete(api)
        this.items = this.items.filter(i => i.id !== id)
        notifyService.success(this.$t('attributes.delete_succeed'))
      } catch (e) {
        notifyService.delete_error(e)
      } finally {
        this.sending = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-card {
  min-width: 960px;
  min-height: 540px;

  .table {
    td {
      vertical-align: middle;

      .control {
        margin: -0.5rem -0.75rem;
      }
    }
  }
}

@media (max-width: 960px) {
  .modal-card {
    min-width: 760px;
  }
}

@media (max-width: 768px) {
  .modal-card {
    min-width: auto;
  }
}
</style>
